import { ReactNode, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import PriorityHighOutlined from '@mui/icons-material/PriorityHighOutlined';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { ShowPageLayout } from 'framework/ui/layout';
import { ApprovalLayout } from 'framework/ui/ui-builder/approval';
import { ErrorMessage, SuccessMessage, useMessageService } from 'framework/ui/Message';
import { DATE_FORMAT, SanitizedHtml } from 'framework/utils';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  useNewProjectUrl,
  useProjectSecurityContext,
} from 'domain/shared/newProject';
import { useEmployeeNamesByAliases } from 'domain/shared/projectHeatmap/useEmployeeNamesByAliases';
import { buildEmployeeFullName } from 'domain/shared/projectHeatmap/employeeName';
import AssumptionBreadcrumbs from '../../shared/breadcrumbs/AssumptionBreadcrumbs';
import useAssumptionsService from '../assumptionsService';
import { ASSUMPTION_EXCEPTION_STATUS, AssumptionExceptionStatus } from '../types';
import { useAssumptionReview } from './AssumptionReviewContext';
import useReconcileProjectNotifications, {
  PROJECT_NOTIFICATION_SCOPE,
} from '../../shared/notifications/useReconcileProjectNotifications';

const EXCEPTION_STATUS_TEXT: Record<AssumptionExceptionStatus, string> = {
  [ASSUMPTION_EXCEPTION_STATUS.PENDING]: 'Exception approval pending',
  [ASSUMPTION_EXCEPTION_STATUS.APPROVED]: 'Exception approved',
  [ASSUMPTION_EXCEPTION_STATUS.REJECTED]: 'Exception rejected',
};

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <Typography variant="subtitle1" mb={1.25}>
    {children}
  </Typography>
);

const FieldLabel = ({ children }: { children: ReactNode }) => (
  <Typography variant="body2" color="textSecondary">
    {children}
  </Typography>
);

const ReviewAssumptionLayout = () => {
  const { assumption, projectId, refetch } = useAssumptionReview();
  const navigate = useNavigate();
  const { getAssumptionsUrl } = useNewProjectUrl();
  const { isAccessGranted } = useProjectSecurityContext();
  const { approveAssumptionException, rejectAssumptionException } = useAssumptionsService();
  const { addMessage } = useMessageService();
  const reconcileNotifications = useReconcileProjectNotifications();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canReview = isAccessGranted(ACCESS_INFO_SECTIONS.ASSUMPTION, ACCESS_INFO_ACTIONS.REVIEW);

  const alias = assumption.updatedBy ?? assumption.createdBy ?? '';
  const aliasList = useMemo(() => (alias ? [alias] : []), [alias]);
  const namesByAlias = useEmployeeNamesByAliases(aliasList);

  const requestedUntil = moment(assumption.exceptionUntilDate).format(DATE_FORMAT.FULL_DATE);
  const statusText = assumption.exceptionStatus ? EXCEPTION_STATUS_TEXT[assumption.exceptionStatus] : null;
  const createdAt = moment(assumption.updatedAt ?? assumption.createdAt).format(DATE_FORMAT.FULL_DATE);
  const createdByName = alias ? buildEmployeeFullName(alias, namesByAlias[alias]) : '';
  const requestedLine = createdAt ? `Requested on ${createdAt}${createdByName ? ` by ${createdByName}` : ''}` : '';

  const handleDecision = async (decide: (id: string) => Promise<unknown>, successText: string, errorText: string) => {
    setIsSubmitting(true);
    try {
      await decide(assumption.id);
      addMessage(new SuccessMessage(successText));
      await reconcileNotifications(projectId, [PROJECT_NOTIFICATION_SCOPE.ASSUMPTIONS], assumption.id).catch(
        () => undefined,
      );
      await refetch();
      navigate(getAssumptionsUrl(projectId));
    } catch {
      addMessage(new ErrorMessage(errorText));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApprove = () =>
    handleDecision(approveAssumptionException, 'Exception request approved.', 'Failed to approve exception request.');

  const handleReject = () =>
    handleDecision(rejectAssumptionException, 'Exception request rejected.', 'Failed to reject exception request.');

  return (
    <ShowPageLayout breadcrumbs={<AssumptionBreadcrumbs />}>
      <ApprovalLayout
        title="Assumption exception request"
        onApprove={canReview ? handleApprove : undefined}
        onReject={canReview ? handleReject : undefined}
        isSubmitting={isSubmitting}
      >
        {statusText && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: 'background.warning',
              borderRadius: 1,
              py: 1.25,
              px: 1.5,
              mb: 2,
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <PriorityHighOutlined sx={{ color: 'border.warning', fontSize: 16 }} />
              <Typography variant="caption" color="warning">
                {statusText}
              </Typography>
            </Box>
            {requestedLine && (
              <Typography variant="caption" color="textSecondary">
                {requestedLine}
              </Typography>
            )}
          </Box>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Box>
            <SectionTitle>Assumption description</SectionTitle>
            <Box
              sx={{
                wordBreak: 'break-word',
                '& p:first-of-type, & ul:first-of-type, & ol:first-of-type': { mt: 0 },
                '& p:last-of-type, & ul:last-of-type, & ol:last-of-type': { mb: 0 },
              }}
            >
              <SanitizedHtml html={assumption.description ?? ''} />
            </Box>
          </Box>
          <Box>
            <SectionTitle>Exception request</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <FieldLabel>Reason</FieldLabel>
                <Typography variant="body1">{assumption.exceptionReason || '—'}</Typography>
              </Box>
              <Box>
                <FieldLabel>Requested exception until</FieldLabel>
                <Typography variant="body1">{requestedUntil}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </ApprovalLayout>
    </ShowPageLayout>
  );
};

export default ReviewAssumptionLayout;
